import React from 'react';

import { Divider, Popover, Typography } from '@mui/material';
import clsx from 'clsx';
import InfiniteScroll from 'react-infinite-scroll-component';
import { SearchInput } from 'src/components/SearchInput';
import { ColorGrid } from 'src/components/workflow/board/settings/ColorPickerPopover';
import { makeStyles } from 'tss-react/mui';

import { LucideIconRenderer } from './LucideIconRenderer';
import type { LucideIcon } from './useLucideIconPicker';
import { useLucideIconPicker } from './useLucideIconPicker';

const useStyles = makeStyles()(theme => ({
  colorSection: {
    padding: theme.spacing(2, 2, 1, 2),
  },
  colorTitle: {
    color: theme.palette.text.primary,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: 449,
  },
  divider: {
    marginTop: theme.spacing(1),
  },
  emptyState: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  iconGridContainer: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(2, 2, 0, 2),
  },
  iconItem: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
    },
    alignItems: 'center',
    borderRadius: 8,
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    display: 'flex',
    fontSize: 24,
    justifyContent: 'center',
    padding: theme.spacing(1),
    transition: 'background-color 0.2s',
  },
  iconItemCustom: {
    '&::after': {
      backgroundColor: theme.palette.primary.main,
      borderRadius: 4,
      bottom: 2,
      color: theme.palette.primary.contrastText,
      content: '"★"',
      fontSize: 8,
      padding: '1px 3px',
      position: 'absolute',
      right: 2,
    },
    position: 'relative',
  },
  iconItemSelected: {
    backgroundColor: theme.appColors.selectedBackground,
  },
  loader: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(12),
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  searchContainer: {
    padding: theme.spacing(2, 2, 1, 2),
  },
  searchInput: {
    backgroundColor: theme.appColors.background,
    borderRadius: 8,
  },
}));

interface LucideIconPickerProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSelect: (iconName: string) => void;
  open: boolean;
  customIcons?: LucideIcon[];
  onColorSelect?: (color: string) => void;
  selectedColor?: string;
  value?: string;
}

export function LucideIconPicker({
  anchorEl,
  customIcons,
  onClose,
  onColorSelect,
  onSelect,
  open,
  selectedColor,
  value,
}: LucideIconPickerProps): JSX.Element {
  const { classes } = useStyles();
  const scrollableId = 'lucide-icon-picker-scroll';
  const { hasMore, icons, loadMore, reset, search, setSearch } = useLucideIconPicker(customIcons);

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom',
      }}
      open={open}
      transformOrigin={{
        horizontal: 'left',
        vertical: 'top',
      }}
      onClose={() => {
        setSearch(''); // Clear search on close
        reset(); // Reset pagination
        onClose();
      }}
    >
      <div className={classes.container}>
        {onColorSelect && (
          <>
            <div className={classes.colorSection}>
              <Typography className={classes.colorTitle}>Color</Typography>
              <ColorGrid selectedColor={selectedColor} onColorSelect={onColorSelect} />
            </div>
            <Divider className={classes.divider} />
          </>
        )}

        <div className={classes.searchContainer}>
          <SearchInput
            autoFocus
            inputClassName={classes.searchInput}
            minWidth="100%"
            placeholder="Search icon"
            search={search}
            setSearch={setSearch}
          />
        </div>

        {icons.length > 0 ? (
          <div className={classes.iconGridContainer} id={scrollableId}>
            <InfiniteScroll
              dataLength={icons.length}
              hasMore={hasMore}
              loader={
                <div className={classes.loader}>
                  <Typography variant="caption">Loading more icons...</Typography>
                </div>
              }
              next={loadMore}
              scrollableTarget={scrollableId}
              style={{
                display: 'grid',
                gap: '8px',
                gridTemplateColumns: 'repeat(6, 1fr)',
              }}
            >
              {icons.map(icon => (
                <div
                  key={icon.name}
                  className={clsx(classes.iconItem, {
                    [classes.iconItemSelected]: value === icon.name,
                    [classes.iconItemCustom]: icon.type === 'custom',
                  })}
                  title={icon.friendlyName}
                  onClick={() => {
                    onSelect(icon.name);
                    setSearch(''); // Clear search after selection
                    reset(); // Reset pagination
                    onClose();
                  }}
                >
                  <LucideIconRenderer icon={icon.name} iconUrl={icon.url} size={24} />
                </div>
              ))}
            </InfiniteScroll>
          </div>
        ) : (
          <div className={classes.emptyState}>
            <Typography variant="body2">No icons found</Typography>
          </div>
        )}
      </div>
    </Popover>
  );
}
